/**
 * SourceMenu - Shared menu content for source actions
 *
 * Used by:
 * - SourcesListPanel (dropdown via "..." button, context menu via right-click)
 * - SourceInfoPage (title dropdown menu)
 *
 * Uses MenuComponents context to render with either DropdownMenu or ContextMenu
 * primitives, allowing the same component to work in both scenarios.
 *
 * Provides consistent source actions:
 * - Open in New Window
 * - Show in file manager
 * - Delete
 */

import * as React from 'react'
import {
  Trash2,
  FolderOpen,
  AppWindow,
  Pencil,
} from 'lucide-react'
import { useMenuComponents } from '@/components/ui/menu-context'
import { isIPad, getFileManagerName } from '@/lib/platform'

export interface SourceMenuProps {
  /** Source slug */
  sourceSlug: string
  /** Source name for display */
  sourceName: string
  /** Whether this is a default (built-in) source that cannot be deleted */
  isDefault?: boolean
  /** Whether this is a protected knowledge collection that cannot be deleted */
  isProtectedKnowledge?: boolean
  /** Optional callback to edit collection metadata (knowledge sources only) */
  onEditCollection?: () => void
  /** Callbacks */
  onOpenInNewWindow: () => void
  onShowInFinder: () => void
  onDelete: () => void
}

/**
 * SourceMenu - Renders the menu items for source actions
 * This is the content only, not wrapped in a DropdownMenu or ContextMenu
 */
export function SourceMenu({
  sourceSlug,
  sourceName,
  isDefault,
  isProtectedKnowledge,
  onEditCollection,
  onOpenInNewWindow,
  onShowInFinder,
  onDelete,
}: SourceMenuProps) {
  // Get menu components from context (works with both DropdownMenu and ContextMenu)
  const { MenuItem, Separator } = useMenuComponents()

  return (
    <>
      {/* Edit Collection — only for knowledge sources */}
      {onEditCollection && (
        <MenuItem onClick={onEditCollection}>
          <Pencil className="h-3.5 w-3.5" />
          <span className="flex-1">Edit Collection</span>
        </MenuItem>
      )}

      {/* Open in New Window — desktop only */}
      {!isIPad && (
        <MenuItem onClick={onOpenInNewWindow}>
          <AppWindow className="h-3.5 w-3.5" />
          <span className="flex-1">Open in New Window</span>
        </MenuItem>
      )}

      {/* Show in file manager — desktop only */}
      {!isIPad && (
        <MenuItem onClick={onShowInFinder}>
          <FolderOpen className="h-3.5 w-3.5" />
          <span className="flex-1">{`Show in ${getFileManagerName()}`}</span>
        </MenuItem>
      )}

      {/* Delete — hidden for default (built-in) sources and protected knowledge collections */}
      {!isDefault && !isProtectedKnowledge && (
        <>
          <Separator />
          <MenuItem onClick={onDelete} variant="destructive">
            <Trash2 className="h-3.5 w-3.5" />
            <span className="flex-1">Delete Source</span>
          </MenuItem>
        </>
      )}
    </>
  )
}
